import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';
import { activityService } from '../../../services/activityService';

const ICON_MAP = {
  task_completed: { icon: 'CheckCircle2', color: 'text-emerald-500 bg-emerald-50' },
  document_uploaded: { icon: 'Upload', color: 'text-blue-500 bg-blue-50' },
  snag_created: { icon: 'Wrench', color: 'text-orange-500 bg-orange-50' },
  snag_resolved: { icon: 'CheckCircle', color: 'text-emerald-500 bg-emerald-50' },
  incident_created: { icon: 'AlertTriangle', color: 'text-red-500 bg-red-50' },
  site_created: { icon: 'Building2', color: 'text-purple-500 bg-purple-50' },
  site_updated: { icon: 'RefreshCw', color: 'text-purple-500 bg-purple-50' },
  worker_assigned: { icon: 'UserPlus', color: 'text-blue-500 bg-blue-50' },
  inspection_completed: { icon: 'ClipboardCheck', color: 'text-emerald-500 bg-emerald-50' },
  photo_evidence_added: { icon: 'Camera', color: 'text-cyan-500 bg-cyan-50' },
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const AuditTrailFeed = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await activityService?.getAllActivities();
        if (result?.error) {
          console.error('[AuditTrailFeed] load activities failed:', { code: result?.error?.code, message: result?.error?.message });
        }
        setActivities((result?.data || [])?.slice(0, 8));
      } catch (err) {
        console.error('[AuditTrailFeed] load activities threw:', err);
      }
      setLoading(false);
    };
    load();
  }, []);

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-foreground font-heading">{t('auditTrail')}</h3>
          <p className="text-sm text-muted-foreground">{t('auditTrailSubtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/compliance-report?tab=audit')}
          className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1"
        >
          {t('fullLog')} <Icon name="ArrowRight" size={14} />
        </button>
      </div>
      {loading ? (
        <div className="px-6 py-8 flex items-center justify-center">
          <Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" />
        </div>
      ) : activities?.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-muted-foreground">
          No activity recorded yet.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {activities?.map((event) => {
            const cfg = ICON_MAP?.[event?.activity_type] || { icon: 'Activity', color: 'text-slate-500 bg-slate-100' };
            return (
              <div
                key={event?.id}
                className="w-full px-6 py-3.5 flex items-start gap-4 hover:bg-muted/50 transition-colors text-left"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${cfg?.color}`}>
                  <Icon name={cfg?.icon} size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{event?.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{event?.description}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                      {formatTimeAgo(event?.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuditTrailFeed;
